import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Button, SkeletonRows } from "../../../../components/ui";
import { focusRing } from "../../../../components/ui/cn";
import { businessApi } from "../../../../services/api";
import { formatPay, formatPayDate, formatPeso, supportsFixedAllowance } from "../compensationView";

// My HR > Pay (owner decision C6): the signed-in employee's own pay agreement. The server resolves the employee;
// no member id is sent. This is the agreement, not a payslip and not a payment.

const panel = "rounded-panel border border-default bg-surface";

function Agreement({ pay, heading, dateLabel }) {
  // Daily and hourly pay has no fixed allowance, so none is ever shown for it.
  const hasAllowance = supportsFixedAllowance(pay.salaryFrequency);
  return (
    <div className={`${panel} p-4`}>
      <h3 className="text-label text-secondary">{heading}</h3>
      <p className="mt-1 text-metric tabular-nums text-foreground">{formatPay(pay.basePayCents, pay.salaryFrequency)}</p>
      <dl className="mt-3 grid gap-3 text-body-sm sm:grid-cols-2">
        <div>
          <dt className="text-meta text-secondary">{dateLabel}</dt>
          <dd className="font-medium text-foreground">{formatPayDate(pay.effectiveFrom)}</dd>
        </div>
        {hasAllowance ? (
          <div>
            <dt className="text-meta text-secondary">Fixed allowance</dt>
            <dd className="font-medium tabular-nums text-foreground">
              {pay.allowanceCents > 0 ? formatPay(pay.allowanceCents, pay.salaryFrequency) : "None"}
            </dd>
          </div>
        ) : null}
      </dl>
      {hasAllowance && pay.allowanceBreakdown?.length ? (
        <ul className="mt-3 space-y-1 border-t border-subtle pt-3 text-body-sm" aria-label="Allowance breakdown">
          {pay.allowanceBreakdown.map((item) => (
            <li key={item.label} className="flex justify-between gap-3 text-secondary">
              <span>{item.label}</span>
              <span className="font-medium tabular-nums text-foreground">{formatPeso(item.amountCents)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function MyPayPanel({ reloadKey = 0 }) {
  const [state, setState] = useState({ loading: true, error: "", pay: null });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const pay = await businessApi.getMyCompensation();
      setState({ loading: false, error: "", pay });
    } catch (err) {
      setState({ loading: false, error: err?.serverMessage || "Your pay could not be loaded.", pay: null });
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const { loading, error, pay } = state;
  const current = pay?.current || null;
  const upcoming = pay?.upcoming || null;

  return (
    <section aria-label="My pay">
      <h2 className="text-section-title text-foreground">My pay</h2>
      <p className="mt-0.5 text-body-sm text-secondary">The pay agreed with your employer.</p>
      <div className="mt-3 space-y-4">
        {loading ? (
          <div className={panel}><SkeletonRows rows={2} columns={2} label="Loading your pay" /></div>
        ) : error ? (
          <div role="alert" className={`${panel} flex flex-col items-start gap-3 p-4 text-body-sm text-foreground sm:flex-row sm:items-center sm:justify-between`}>
            <span>{error}</span>
            <Button size="sm" onClick={load}>Try again</Button>
          </div>
        ) : (
          <>
            {current ? (
              <Agreement pay={current} heading="Current pay" dateLabel="In effect since" />
            ) : (
              <div className={`${panel} p-4 text-body-sm text-secondary`}>
                <p className="text-panel-title text-foreground">
                  {upcoming ? "Your pay has not started yet." : "No pay has been set for you yet."}
                </p>
                <p className="mt-1">
                  {upcoming
                    ? "It begins on the date shown below."
                    : "HR sets your pay. If you expected to see it here, ask your HR contact."}
                </p>
              </div>
            )}
            {upcoming ? <Agreement pay={upcoming} heading={current ? "Upcoming change" : "Starting pay"} dateLabel="Starts on" /> : null}
            <p className="max-w-[75ch] text-body-sm text-secondary">
              This is your pay agreement. What you were actually paid for each period, with deductions, is on your payslips.{" "}
              <Link to="/business/hr/my-payslips" className={`rounded-control font-medium text-accent hover:underline ${focusRing}`}>Open My Payslips</Link>
            </p>
          </>
        )}
      </div>
    </section>
  );
}
